import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Award, Target, Clock, TrendingUp, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WorkoutPrograms = ({ token }) => {
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [enrolledPrograms, setEnrolledPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPrograms();
    fetchEnrolledPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const response = await axios.get(`${API}/exercise/programs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrograms(response.data.programs || []);
    } catch (error) {
      console.error('Error fetching programs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEnrolledPrograms = async () => {
    try {
      const response = await axios.get(`${API}/exercise/enrolled-programs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEnrolledPrograms(response.data.programs || []);
    } catch (error) {
      console.error('Error fetching enrolled programs:', error);
    }
  };

  const enrollInProgram = async (programId) => {
    try {
      await axios.post(
        `${API}/exercise/enroll-program`,
        { program_id: programId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('✅ Enrolled in program!');
      fetchEnrolledPrograms();
    } catch (error) {
      console.error('Error enrolling:', error);
      alert('❌ Failed to enroll');
    }
  };

  const isEnrolled = (programId) => {
    return enrolledPrograms.some(ep => ep.program_id === programId);
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6600] mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="workout-programs">
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm mb-4">
        <h2 className="text-lg font-bold mb-2 flex items-center gap-2">
          <Award size={20} className="text-[#FF6600]" />
          Workout Programs
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Choose from our professionally designed workout programs
        </p>
      </div>

      {/* Programs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {programs.map((program) => (
          <div
            key={program.id}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow"
          >
            <div className={`h-1 bg-gradient-to-r ${
              program.level === 'beginner' ? 'from-green-400 to-green-600' :
              program.level === 'intermediate' ? 'from-blue-400 to-blue-600' :
              'from-purple-400 to-purple-600'
            }`} />

            <div className="p-4">
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-bold">{program.name}</h3>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  program.level === 'beginner' ? 'bg-green-100 text-green-700' :
                  program.level === 'intermediate' ? 'bg-blue-100 text-blue-700' :
                  'bg-purple-100 text-purple-700'
                }`}>
                  {program.level}
                </span>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">{program.description}</p>

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                  <Clock size={14} className="text-[#FF6600]" />
                  <span>{program.duration} weeks</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                  <Target size={14} className="text-[#FF6600]" />
                  <span>{program.goal}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                  <TrendingUp size={14} className="text-[#FF6600]" />
                  <span>{program.frequency} days/week</span>
                </div>
              </div>

              {isEnrolled(program.id) ? (
                <button
                  disabled
                  className="w-full py-3 bg-green-500 text-white rounded-lg font-semibold flex items-center justify-center gap-2"
                >
                  <CheckCircle size={16} />
                  Enrolled
                </button>
              ) : (
                <button
                  onClick={() => enrollInProgram(program.id)}
                  className="w-full py-3 bg-[#FF6600] text-white rounded-lg hover:bg-[#E55C00] font-semibold"
                >
                  Enroll Now
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Enrolled Programs */}
      {enrolledPrograms.length > 0 && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
          <h3 className="font-bold mb-4">My Active Programs</h3>
          <div className="space-y-4">
            {enrolledPrograms.map((ep) => {
              const program = programs.find(p => p.id === ep.program_id);
              if (!program) return null;

              const progress = (ep.current_week / program.duration) * 100;

              return (
                <div key={ep.id} className="border-2 border-gray-200 dark:border-gray-700 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold">{program.name}</h4>
                    <span className="text-xs text-gray-600 dark:text-gray-300">
                      Week {ep.current_week} of {program.duration}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                    <div
                      className="bg-[#FF6600] h-2 rounded-full transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-300">{Math.round(progress)}% complete</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkoutPrograms;
